import React, { useEffect } from 'react';
import { Col, Table, Spinner } from 'reactstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTableHook, useConfirmationModal } from '../../hooks';
import {
  cancelRequest,
  fetchMyRequests,
  fetchRequestTypes,
  fetchTableColumnValues,
} from '../../../store/apis/approvalService';
import {
  FilterDropdown,
  SelectInput,
  TableFilterBubbles,
  TableSortDisplayArrow,
  TablePagination,
} from '../../Common';
import { myRequestsHeaders, renderRequestLegend } from '../constants';
import * as icons from '../../../assets';
import { Messages } from '../../../common/messages';
import './MyRequest.scss';

function MyRequests({ setCounts, handleEditClick }) {
  const queryClient = useQueryClient();
  const { open, ConfirmationModal } = useConfirmationModal();

  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    handleSetExternalFilters,
    applyExternalFilters,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    isLoadingData,
    isLoadingColumn,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'RequestDate',
      sortDirection: 'DESC',
      filters: [],
    },
    fetchFn: fetchMyRequests,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'MyRequests',
    columnKeyPrefix: 'MyRequestsColumn',
    tableName: 'MyRequests',
    defaultExternalFilters: {
      category: '',
    },
    tableQueryOptions: {
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    },
  });

  // Set Counts
  useEffect(() => {
    setCounts(prev => ({
      ...prev,
      myRequests: data?.totalCount || 0,
    }));
  }, [data?.totalCount, setCounts]);

  const { data: requestTypes } = useQuery({
    queryKey: ['requestTypes'],
    queryFn: fetchRequestTypes,
    keepPreviousData: true,
  });

  const cancelRequestMutation = useMutation({
    mutationFn: cancelRequest,
    onSuccess: () => {
      toast.success('Request cancelled successfully', {
        className: 'toast-success',
      });
      queryClient.invalidateQueries(['MyRequests']);
    },
  });

  const mapCancelRequest = request => ({
    requestId: request.RequestID,
    category: request.RequestType,
    typ: request?.Typ,
    approvalRank: request?.ApprovalRank,
    requestedDate: request.RequestDate,
    requestType: request.RequestType,
    details: request.Details,
    approver: request.Approver,
    status: request.Status,
  });

  // Function to handle the cancel request
  const handleCancelRequest = async request => {
    const cancelData = mapCancelRequest(request);
    cancelRequestMutation.mutate(cancelData);
  };

  const renderTableBody = () => {
    const tableData = data?.data;

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={6}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_REQUESTS
            )}
          </td>
        </tr>
      );
    }

    return tableData.map((item, index) => (
      <tr
        key={index}
        className="cursor-pointer"
        onClick={() => handleEditClick(item)}
      >
        <td>{item.RequestID}</td>
        <td>{item.RequestDate}</td>
        <td>{renderRequestLegend(item.RequestType)}</td>
        <td
          style={{
            textOverflow: 'ellipsis',
            overflowX: 'hidden',
          }}
        >
          {item.Details.replace(/(\d+\.\d{2})/, match =>
            Number(match).toLocaleString('en-US', { minimumFractionDigits: 2 }),
          )}
        </td>
        <td>{item.Approver}</td>
        <td>{item.Status}</td>
        <td className="text-center">
          <div className="d-flex flex-row align-items-center justify-content-center gap-2">
            <button
              type="button"
              className="non-btn"
              title="View details of my pending request."
            >
              <img src={icons.iconsPreviewRequestIcon} height={17} alt="" />
            </button>
            <button
              type="button"
              className="non-btn"
              onClick={e => {
                e.stopPropagation();
                open({
                  title: 'Cancel Request',
                  message: 'Are you sure you want to cancel request?',
                  onConfirm: () => handleCancelRequest(item),
                  showCancelButton: true,
                });
              }}
              title="Cancel your request before it has been approved."
            >
              <img
                src={icons.iconsTrash}
                alt=""
                className="reject-svg"
                height={17}
              />
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  const generateRequestTypes = () => {
    if (!requestTypes || requestTypes.length === 0) {
      return [];
    }
    const requestTypeItems = requestTypes?.map(item => ({
      Code: item.Code,
      Name: item.Code,
    }));
    requestTypeItems.unshift({ Code: '', Name: 'All' });
    return requestTypeItems;
  };

  return (
    <div className="my-requests">
      <div className="external-filter d-flex justify-content-between gap-2 mx-2 mt-2 p-3">
        <Col lg={3} sm={3} md={3} className="external-filter-col">
          <b>Request Type</b>
          <SelectInput
            data={generateRequestTypes()}
            key="Code"
            labelKey="Name"
            valueKey="Code"
            onChange={e => handleSetExternalFilters('category', e.target.value)}
            placeholder="Select Request Type"
            value={externalFilters?.category}
            hasDefault
          />
        </Col>
        <Col lg={3}>
          <div className="external-filter-btns justify-content-end gap-2">
            <button
              type="button"
              className="custom-btn"
              onClick={() => applyExternalFilters()}
            >
              Search
            </button>
          </div>
        </Col>
      </div>
      <div className="my-2 px-2">
        <TableFilterBubbles
          allFilters={pageRequest?.filters || []}
          clearSingleFilterValue={clearSingleFilterValue}
          labelMapping={myRequestsHeaders}
          clearAllFilters={resetFilters}
        />
      </div>
      <div className="table-responsive my-requests-table-container">
        <Table
          hover
          className="custom-table align-middle text-nowrap"
          id="request-table"
        >
          <thead>
            <tr>
              {Object.entries(myRequestsHeaders).map(([key, label]) => (
                <th key={key}>
                  <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                    <div
                      className="p-0 cursor-pointer"
                      onClick={() => handleAutoSort(key)}
                    >
                      {label}
                    </div>
                    <TableSortDisplayArrow
                      direction={checkColumnIfSorted(key)}
                    />
                    <FilterDropdown
                      handleSetColumnName={() => {
                        setColumnName(key);
                      }}
                      setColumnName={setColumnName}
                      columnData={columnData}
                      applyFilter={applyFilter}
                      columnName={columnName}
                      pageRequest={pageRequest}
                      setPageRequest={setPageRequest}
                      handleManualSort={handleManualSort}
                      isLoading={isLoadingColumn}
                    />
                  </div>
                </th>
              ))}
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>{renderTableBody()}</tbody>
        </Table>
      </div>
      <ConfirmationModal />
      <div className="py-2 px-3 border-top mt-auto">
        <TablePagination
          nextPage={nextPage}
          prevPage={prevPage}
          totalPages={data?.totalPages}
          currentPage={data?.currentPage}
          hasPreviousPage={data?.hasPreviousPage}
          hasNextPage={data?.hasNextPage}
          getPageSize={getPageSize}
          setPageSize={setPageSize}
          totalCount={data?.totalCount}
        />
      </div>
    </div>
  );
}

export default MyRequests;
